import { Link } from '@tanstack/react-router';
import { Plus, Users, BookOpen, Archive } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import { SCHOOL_LEVELS, SUBJECTS } from '@jeraisonne/db';
import type { SchoolLevel, SubjectSlug } from '@jeraisonne/db';
import { useAuth } from '@/lib/auth';
import { useTutorClasses } from '@/lib/tutor/queries';
import type { ClassSummary } from '@/lib/tutor/queries';

const LEVEL_LABEL = Object.fromEntries(SCHOOL_LEVELS.map((l) => [l.value, l.label]));
const SUBJECT_LABEL = Object.fromEntries(SUBJECTS.map((s) => [s.value, s.label]));

export function TutorClassesPage() {
  const { user } = useAuth();
  const tutorId = user?.id ?? '';
  const { data: classes, isLoading } = useTutorClasses(tutorId);

  const active = (classes ?? []).filter((c) => !c.archived_at);
  const archived = (classes ?? []).filter((c) => !!c.archived_at);

  return (
    <div className="mx-auto max-w-4xl p-6 md:p-8">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between">
        <div>
          <p className="font-display text-accent-500 text-[11px] font-semibold uppercase tracking-[.14em]">
            Classes
          </p>
          <h1 className="font-display text-ink-900 mt-1 text-2xl font-extrabold tracking-tight md:text-[26px]">
            Mes classes
          </h1>
          <p className="text-ink-500 mt-1 font-sans text-sm">
            Chaque classe a son code d'invitation. Les élèves s'y connectent depuis l'app.
          </p>
        </div>
        <Link
          to="/tutor/classes/new"
          className="bg-primary-500 font-display hover:bg-primary-600 inline-flex h-11 shrink-0 items-center gap-2 rounded-xl px-5 text-sm font-bold text-white shadow-md transition-all active:scale-[.98]"
        >
          <Plus className="h-4 w-4" />
          Nouvelle classe
        </Link>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {[1, 2].map((i) => (
            <div key={i} className="bg-ink-100 h-40 animate-pulse rounded-2xl" />
          ))}
        </div>
      ) : active.length === 0 ? (
        <EmptyClasses />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {active.map((cls) => (
            <ClassCard key={cls.id} cls={cls} />
          ))}
        </div>
      )}

      {archived.length > 0 && (
        <div className="mt-10">
          <h2 className="font-display text-ink-500 mb-3 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[.1em]">
            <Archive className="h-4 w-4" /> Archivées ({archived.length})
          </h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {archived.map((cls) => (
              <ClassCard key={cls.id} cls={cls} archived />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ClassCard({ cls, archived = false }: { cls: ClassSummary; archived?: boolean }) {
  return (
    <Link
      to="/tutor/classes/$classId"
      params={{ classId: cls.id }}
      className={cn(
        'block rounded-2xl border bg-white transition-all hover:shadow-md active:scale-[.99]',
        archived ? 'border-ink-200 opacity-70' : 'border-ink-200 hover:border-primary-300',
      )}
    >
      <div className="p-5">
        {/* Badges */}
        <div className="mb-3 flex items-center gap-2">
          {cls.level && (
            <span className="bg-primary-100 text-primary-700 font-display inline-flex h-6 items-center rounded-full px-2.5 text-[11px] font-bold uppercase tracking-[.06em]">
              {LEVEL_LABEL[cls.level as SchoolLevel] ?? cls.level}
            </span>
          )}
          {cls.subject && (
            <span className="font-display text-ink-500 text-[11px] font-semibold uppercase tracking-[.1em]">
              {SUBJECT_LABEL[cls.subject as SubjectSlug] ?? cls.subject}
            </span>
          )}
        </div>

        {/* Name */}
        <h3 className="font-display text-ink-900 text-lg font-extrabold leading-tight">
          {cls.name}
        </h3>
        {cls.description && (
          <p className="text-ink-500 mt-1 line-clamp-2 font-sans text-sm">{cls.description}</p>
        )}

        {/* Footer */}
        <div className="border-ink-100 mt-4 flex items-center justify-between border-t pt-4">
          <div className="text-ink-500 flex items-center gap-1.5 text-sm">
            <Users className="h-4 w-4" />
            <span className="font-display text-ink-900 font-bold">{cls.member_count}</span>
            <span className="font-sans">élève{cls.member_count !== 1 ? 's' : ''}</span>
          </div>
          <span className="text-ink-500 bg-ink-100 rounded-lg px-2.5 py-1 font-mono text-xs">
            {cls.invite_code}
          </span>
        </div>
      </div>
    </Link>
  );
}

function EmptyClasses() {
  return (
    <div className="border-ink-200 rounded-2xl border bg-white p-10 text-center">
      <div className="bg-primary-50 mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl">
        <BookOpen className="text-primary-500 h-7 w-7" />
      </div>
      <h3 className="font-display text-ink-900 mb-1 text-lg font-extrabold">Aucune classe</h3>
      <p className="text-ink-500 mx-auto mb-6 max-w-xs font-sans text-sm">
        Crée ta première classe et donne le code d'invitation à tes élèves.
      </p>
      <Link
        to="/tutor/classes/new"
        className="bg-primary-500 font-display hover:bg-primary-600 inline-flex h-10 items-center gap-2 rounded-xl px-5 text-sm font-bold text-white shadow-md transition-all"
      >
        <Plus className="h-4 w-4" />
        Créer une classe
      </Link>
    </div>
  );
}
